
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IProvider } from "@/models/providers";
import {
  IClientCProdsProviderData,
  IClientCProdsProviderMethods
} from "@/models/providers/contractProduct";
import { IClient } from "@/models/clients";
import { IState } from "@/store";
import { ICProdGroup } from "@/models/contracts";

import { IProductCategory } from "@/models/products";

@Component({
  computed: {
    ...mapState({
      cProdGroupsScope() {
        return `$client::${this.clientId}_groups`;
      },
      cProdGroups(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.CONTRACTS_PRODUCTS,
          scope: this.cProdGroupsScope
        });
      },
      cProdCategoriesScope() {
        return `$client::${this.clientId}`;
      },
      cProdCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.CONTRACTS_CATEGORIES,
          scope: this.cProdCategoriesScope
        });
      }
    })
  }
})
export default class CProdsGroupingProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Map state

  public cProdGroups!: ICProdGroup[];
  public cProdGroupsScope!: string;
  public cProdCategories!: IProductCategory[];
  public cProdCategoriesScope!: string;

  // Props

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;
  public showAllIdentifiedServices = false;
  public showAllUnidentifiedServices = false;

  // Life-cycle methods

  public async created() {
    if (!this.$userCan("list_contracts_products")) return;
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CONTRACTS_PRODUCTS,
      scope: this.cProdGroupsScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CONTRACTS_CATEGORIES,
      scope: this.cProdCategoriesScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([this.getCProdGroups()]);
      this.getCProdCategories(); // We purposely do NOT await this
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  /**
   * @desc Here we group and count contract products by `service_identifier` (eg
   * a domain or hostname) AND by category. We then re-group and map this data
   * to create groupings by identifier and category (for products with no identifier)
   */

  public getCProdGroups() {
    return this.$store.dispatch(
      `data/${DataModules.CONTRACTS_PRODUCTS}/groupAndCount`,
      {
        vm: this,
        scope: this.cProdGroupsScope,
        ignoreStored: this.isReloading,
        clientId: this.clientId
      }
    );
  }

  public getCProdCategories() {
    return this.$store.dispatch(
      `data/${DataModules.CONTRACTS_CATEGORIES}/list`,
      {
        vm: this,
        scope: this.cProdCategoriesScope,
        ignoreStored: this.isReloading,
        params: {
          exclude_delegated: 1
        }
      }
    );
  }

  public toggleAllIdentifiedServices() {
    this.showAllIdentifiedServices = true;
  }

  public toggleAllUnidentifiedServices() {
    this.showAllUnidentifiedServices = true;
  }

  // Computed

  public get gridCellLimit() {
    if (this.$store.getters["ui/isWidescreenPlus"]) return 16;
    if (this.$store.getters["ui/isTabletPlus"]) return 12;
    return 6;
  }

  public get allServices() {
    return _.map(
      this.cProdGroups,
      ({ category_id, service_identifier, total }) => {
        const category = _.find(this.cProdCategories, ["id", category_id]);
        return {
          service_identifier,
          category_id,
          category,
          category_name: category?.name_translated || category?.name || "…",
          total
        };
      }
    );
  }

  public get allServicesCount() {
    return _.sumBy(this.allServices, "total");
  }

  public get identifiedServices() {
    const services = _.filter(this.allServices, ({ service_identifier }) => {
      return !!service_identifier;
    });
    return _.map(_.groupBy(services, "service_identifier"), grouped => {
      const { service_identifier } = grouped[0];
      return {
        service_identifier,
        categories: _.map(_.uniqBy(grouped, "category_id"), "category"),
        total: _.sumBy(grouped, "total")
      };
    });
  }

  public get identifiedServicesCount() {
    return _.sumBy(this.identifiedServices, "total");
  }

  public get identifiedServicesLimit() {
    if (this.identifiedServices?.length <= this.gridCellLimit) return 0;
    if (this.showAllIdentifiedServices) return 0;
    return this.gridCellLimit;
  }

  public get identifiedServicesVisible() {
    const limit = this.identifiedServicesLimit;
    if (!limit) return this.identifiedServices;
    return this.identifiedServices.slice(0, limit - 1);
  }

  public get unidentifiedServices() {
    return _.orderBy(
      _.filter(this.allServices, ({ service_identifier }) => {
        return !service_identifier;
      }),
      ["category.level", "category.order"],
      ["asc", "asc"]
    );
  }

  public get unidentifiedServicesCount() {
    return _.sumBy(this.unidentifiedServices, "total");
  }

  public get unidentifiedServicesLimit() {
    if (this.unidentifiedServices?.length <= this.gridCellLimit) return 0;
    if (this.showAllUnidentifiedServices) return 0;
    return this.gridCellLimit;
  }

  public get unidentifiedServicesVisible() {
    // Here we return an empty result set until we have category data loaded
    if (!this.cProdCategories?.length) return [];
    const limit = this.unidentifiedServicesLimit;
    if (!limit) return this.unidentifiedServices;
    return this.unidentifiedServices.slice(0, limit - 1);
  }

  // Provide

  @Provide("clientCProdsProviderData")
  public providerData(): IClientCProdsProviderData {
    return {
      allServices: this.allServices,
      allServicesCount: this.allServicesCount,
      hasRequestError: this.hasRequestError,
      identifiedServices: this.identifiedServicesVisible,
      identifiedServicesCount: this.identifiedServicesCount,
      identifiedServicesLimit: this.identifiedServicesLimit,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      unidentifiedServices: this.unidentifiedServicesVisible,
      unidentifiedServicesCount: this.unidentifiedServicesCount,
      unidentifiedServicesLimit: this.unidentifiedServicesLimit
    };
  }

  @Provide("clientCProdsProviderMethods")
  public get providerMethods(): IClientCProdsProviderMethods {
    return {
      reloadData: this.reloadData,
      toggleAllIdentifiedServices: this.toggleAllIdentifiedServices,
      toggleAllUnidentifiedServices: this.toggleAllUnidentifiedServices
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.$userCan("list_contracts_products"))
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: this.reloadData }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $clientCProdsData: this.providerData(),
        $clientCProdsMethods: this.providerMethods
      })
    );
  }
}
